import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase, clearAuthCookie } from "../supabaseClient";
import { sharedStorage } from "../utils/sharedStorage";
import { updateDeviceActivity, getCurrentDeviceId, registerDevice } from "../utils/deviceManager";

const LAST_ACTIVE_KEY = 'fozzil_main_last_active';
const sharedInactivityStorage = sharedStorage(LAST_ACTIVE_KEY);

const AuthContext = createContext();

const notifySecurityChange = async (user, alertType) => {
  if (!user || !alertType) return;

  try {
    const { data: profile } = await supabase
      .from('profiles')
      .select('first_name, last_name')
      .eq('id', user.id)
      .maybeSingle();

    const fullName = profile
      ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
      : user.user_metadata?.full_name;

    const customerName = fullName || user.email.split('@')[0];

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://fpaybeshadesecure.fozzilindustries.workers.dev';
    await fetch(`${backendUrl}/api/ecosystem/notify-security`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        targetEmail: user.email,
        alertType,
        data: {
          customerName,
          customerEmail: user.email
        }
      })
    });
  } catch (err) {
    console.error("Security notification failed:", err);
  }
};

const notifyNewLogin = async (user) => {
  if (!user || !user.email) return;

  // Deduplication check
  const notifyKey = `fozzil_notified_main_${user.id}`;
  if (sessionStorage.getItem(notifyKey)) {
    return;
  }
  // Set flag immediately to prevent parallel triggers
  sessionStorage.setItem(notifyKey, "true");

  try {
    // Precise Name Fetching Logic
    const { data: profile } = await supabase
      .from('profiles')
      .select('first_name, last_name')
      .eq('id', user.id)
      .maybeSingle();

    const fullName = profile
      ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
      : user.user_metadata?.full_name;

    const customerName = fullName || user.email.split('@')[0];

    // Basic Browser/Device Detection
    const ua = navigator.userAgent;
    let browserName = "Unknown Browser";
    let deviceName = "Web Device";

    if (ua.includes("Firefox")) browserName = "Firefox";
    else if (ua.includes("Chrome")) browserName = "Chrome";
    else if (ua.includes("Safari")) browserName = "Safari";
    else if (ua.includes("Edge")) browserName = "Edge";

    if (ua.includes("iPhone")) deviceName = "iPhone";
    else if (ua.includes("iPad")) deviceName = "iPad";
    else if (ua.includes("Android")) deviceName = "Android Phone";
    else if (ua.includes("Macintosh")) deviceName = "Mac";
    else if (ua.includes("Windows")) deviceName = "Windows PC";

    // Detect Location via IP with timeout
    let location = "Unknown Location";
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const geoResponse = await fetch('https://get.geojs.io/v1/ip/geo.json', { signal: controller.signal });
      clearTimeout(timeoutId);

      const geoData = await geoResponse.json();
      if (geoData.city && geoData.country) {
        location = `${geoData.city}, ${geoData.country}`;
      }
    } catch (e) {
      // Silent catch to keep console clean
    }

    const dateTime = new Date().toLocaleString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }) + " UTC";

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://fpaybeshadesecure.fozzilindustries.workers.dev';
    console.log(`[AuthContext] Attempting to notify login via ${backendUrl}...`);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s timeout for backend

      const response = await fetch(`${backendUrl}/api/ecosystem/notify-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          targetEmail: user.email,
          details: {
            customerName,
            customerEmail: user.email,
            deviceName,
            browserName,
            location,
            dateTime
          }
        })
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        console.log("[AuthContext] Login notification sent successfully.");
      } else {
        const errorText = await response.text();
        console.warn("[AuthContext] Login notification failed on server:", errorText);
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.warn("[AuthContext] Login notification timed out after 15s.");
      } else {
        console.error("[AuthContext] Login notification fetch failed:", err);
      }
    }
  } catch (err) {
    console.error("Login notification failed:", err);
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false); // New state for logout UI

  // Lock to prevent listener interference during hash processing
  const isProcessingHash = React.useRef(false);

  useEffect(() => {
    // Check initial session
    const checkUser = async () => {
      try {
        setIsLoading(true);

        // --- PRE-CHECK: 15-MINUTE INACTIVITY EXPIRY ---
        const INACTIVITY_LIMIT = 15 * 60 * 1000; // 15 minutes
        
        // Use shared cookie instead of localStorage for cross-subdomain sync
        let lastActive = sharedInactivityStorage.getSimple(LAST_ACTIVE_KEY);
        
        // Migration: check localStorage once
        if (!lastActive) {
          const legacy = localStorage.getItem(LAST_ACTIVE_KEY);
          if (legacy) {
            lastActive = legacy;
            sharedInactivityStorage.setSimple(LAST_ACTIVE_KEY, legacy);
            localStorage.removeItem(LAST_ACTIVE_KEY);
          }
        }

        const hasNewAuthToken = window.location.hash.includes('access_token');

        if (lastActive && !hasNewAuthToken) {
          const lastActiveTime = parseInt(lastActive, 10);
          const elapsed = Date.now() - lastActiveTime;
          const IS_STALE_JUNK = elapsed > (12 * 60 * 60 * 1000); 

          if (elapsed > INACTIVITY_LIMIT && !IS_STALE_JUNK) {
            // Session expired — silently clear everything
            sharedInactivityStorage.removeItem(LAST_ACTIVE_KEY);
            clearAuthCookie();
            localStorage.removeItem('fozzil-main-auth-token');
            setUser(null);
            setIsAuthenticated(false);
            setIsLoading(false);
            return;
          }
        }
        // --- TOKEN HANDOFF HANDLING ---
        const hash = window.location.hash.substring(1);
        if (hash) {
          const params = new URLSearchParams(hash);
          const accessToken = params.get("access_token");
          const refreshToken = params.get("refresh_token");

          if (accessToken && refreshToken) {
            // Check if we've already processed these exact tokens AND have a valid session
            const tokenKey = `processed_token_${accessToken.substring(0, 20)}`;
            const alreadyProcessed = sessionStorage.getItem(tokenKey);

            // Even if "processed", check if we actually have a session
            // If not, we need to clear the flag and try again
            if (alreadyProcessed) {
              const { data: { session: existingSession } } = await supabase.auth.getSession();
              if (existingSession?.user) {
                window.history.replaceState(null, "", window.location.pathname + window.location.search);
                setUser(existingSession.user);
                setIsAuthenticated(true);
                setIsLoading(false);
                return;
              } else {
                sessionStorage.removeItem(tokenKey);
              }
            }

            isProcessingHash.current = true; // LOCK

            // Clear hash IMMEDIATELY to prevent any other component from re-reading it
            window.history.replaceState(null, "", window.location.pathname + window.location.search);

            // Try setSession first
            let sessionError = null;
            let handoffSession = null;

            const { data: setData, error: setSessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });

            if (!setSessionError && setData?.session) {
              handoffSession = setData.session;
            } else {
              // Fallback: Try refreshSession with just the refresh token
              // This can work when the access token is expired but refresh token is valid
              const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession({
                refresh_token: refreshToken,
              });

              if (!refreshError && refreshData?.session) {
                handoffSession = refreshData.session;
              } else {
                sessionError = refreshError || setSessionError;
              }
            }

            if (handoffSession?.user) {
              // Mark tokens as processed ONLY after successful session
              sessionStorage.setItem(tokenKey, "true");
              // Clean up old token flags (keep only last 5)
              const allKeys = Object.keys(sessionStorage).filter(k => k.startsWith('processed_token_'));
              if (allKeys.length > 5) {
                allKeys.slice(0, -5).forEach(k => sessionStorage.removeItem(k));
              }

              setUser(handoffSession.user);
              setIsAuthenticated(true);
              
              // Refresh last active with shared cookie
              sharedInactivityStorage.setSimple(LAST_ACTIVE_KEY, Date.now().toString());

              // Auto-activate 'fozzil-main' project
              supabase
                .from('project_activations')
                .upsert(
                  { user_id: handoffSession.user.id, project_id: 'fozzil-main' },
                  { onConflict: 'user_id, project_id' }
                )
                .then(({ error }) => {
                  // Silent activation
                });

              isProcessingHash.current = false; // UNLOCK
              setIsLoading(false);
              notifyNewLogin(handoffSession.user);
              return;
            } else {
              // Clear the processed flag so we can retry with new tokens
              sessionStorage.removeItem(tokenKey);
              isProcessingHash.current = false; // UNLOCK on error
            }
          }
        }

        // --- STANDARD SESSION CHECK ---
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          if (!isAuthenticated) {
            setUser(null);
            setIsAuthenticated(false);
          }
        } else if (session?.user) {
          // --- ZOMBIE SESSION CHECK ---
          // If we just logged out (flag present) and this isn't a new login via hash (no hash),
          // then this is a "zombie" session that shouldn't be here. Purge it.
          const justLoggedOut = sessionStorage.getItem("fozzil_just_logged_out") === "true";
          const hasNewAuthToken = window.location.hash.includes("access_token");

          if (justLoggedOut && !hasNewAuthToken) {
            // IMPORTANT: Clear the flag FIRST to prevent loop
            sessionStorage.removeItem("fozzil_just_logged_out");

            await supabase.auth.signOut(); // Force sign out on the client
            clearAuthCookie();
            localStorage.removeItem('fozzil-main-auth-token');
            localStorage.removeItem('fozzil_device_id');
            localStorage.removeItem('fozzil_session_token');

            for (const key of Object.keys(localStorage)) {
              if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
                localStorage.removeItem(key);
              }
            }

            setUser(null);
            setIsAuthenticated(false);
            setIsLoading(false);
            return; // Stop here - don't continue to set authenticated
          } else {
            // Clear flag if we have a new auth token (successful new login)
            if (hasNewAuthToken) {
              sessionStorage.removeItem("fozzil_just_logged_out");
              notifyNewLogin(session.user); // Trigger notification for new login via hash
            }
            setUser(session.user);
            setIsAuthenticated(true);
            // Update activity with shared cookie
            sharedInactivityStorage.setSimple(LAST_ACTIVE_KEY, Date.now().toString());
          }
        } else {
          if (!isAuthenticated) {
            setUser(null);
            setIsAuthenticated(false);
          }
        }
      } catch (error) {
        setUser(null);
        setIsAuthenticated(false);
        isProcessingHash.current = false;
      } finally {
        setIsLoading(false);
      }
    };

    checkUser();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // If we are explicitly processing a hash, ignore listener updates to prevent race conditions
      if (isProcessingHash.current) {
        return;
      }

      // Auth state change handled silently
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecovering(true);
      }

      if (session?.user) {
        setUser(session.user);
        setIsAuthenticated(true);

        // Trigger sign-in notification on any SIGNED_IN event
        if (event === 'SIGNED_IN') {
          notifyNewLogin(session.user);
        }
      } else {
        // Only clear user if we are not loading.
        if (!isLoading) {
          setUser(null);
          setIsAuthenticated(false);
        }
      }
      setIsLoading(false);
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Global device heartbeat — keeps the device showing 'Online now'
  // Runs across the entire Main Website, not just the Account page
  useEffect(() => {
    if (!user || !isAuthenticated) return;

    let heartbeat;

    const initializeDeviceAndHeartbeat = async () => {
      // 1. Ensure the device is registered in the database for this auth session
      // Even if it already exists, registering handles updating the session token & location naturally
      await registerDevice(user.id);

      // 2. Perform the initial immediate activity heartbeat update
      const deviceId = getCurrentDeviceId();
      if (deviceId) {
        updateDeviceActivity();
      }

      // 3. Start the looping 2-minute heartbeat
      heartbeat = setInterval(() => {
        const id = getCurrentDeviceId();
        if (id) updateDeviceActivity();
      }, 2 * 60 * 1000);
    };

    initializeDeviceAndHeartbeat();

    return () => {
      if (heartbeat) {
        clearInterval(heartbeat);
      }
    };
  }, [user, isAuthenticated]);

  const login = async (email, password) => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw error;
      }

      setUser(data.user);
      setIsAuthenticated(true);
      return data;
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoggingOut(true); // Show overlay

      // 1. Sign out locally for Main Website + Account + Store
      // Clear the shared cookie and inactivity key
      clearAuthCookie();
      sharedInactivityStorage.removeItem(LAST_ACTIVE_KEY);
      localStorage.removeItem('fozzil-main-auth-token');

      // Also clear any legacy keys
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
          localStorage.removeItem(key);
        }
      }

      // 2. Clear sign-in notification flags so next login triggers email
      for (const key of Object.keys(sessionStorage)) {
        if (key.startsWith('fozzil_notified_')) {
          sessionStorage.removeItem(key);
        }
      }

      // 3. Set FLAG to prevent auto-redirect loop upon re-rendering Login page
      sessionStorage.setItem("fozzil_just_logged_out", "true");

      // 3. Defer state clearing
      // We intentionally DO NOT clear user/isAuthenticated state here.
      // Doing so would trigger ProtectedRoute to immediately redirect to Auth Hub
      // before we can perform the final window.location.href redirect to the homepage.
      // The page reload at step 4 will naturally reset the state to logged out.

      // 4. Optional delay for UX
      await new Promise(resolve => setTimeout(resolve, 1000));

      // 5. GLOBAL LOGOUT (Redirect to Central Auth Hub)
      // This is critical to kill the session on port 3005 so auto-login doesn't happen when we return.
      // Keep isLoggingOut=true so the dark overlay stays visible until the browser navigates away.
      const authHubLogoutUrl = (process.env.REACT_APP_AUTH_URL || "https://auth.fozzil.in") + "/logout";
      const returnUrl = window.location.origin; // e.g. http://localhost:3001
      let deviceId = localStorage.getItem('fozzil_did');
      if (!deviceId) {
        deviceId = crypto.randomUUID();
        localStorage.setItem('fozzil_did', deviceId);
      }
      const payload = btoa(JSON.stringify({ t: Date.now(), r: returnUrl, c: 'fozzil_main', d: deviceId }));
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const cookieDomain = isLocalhost ? '' : 'domain=.fozzil.in;';
      document.cookie = `shade_token=${payload}; ${cookieDomain} path=/; max-age=60; secure; samesite=lax`;
      
      const devAuth = isLocalhost ? '&dev_key=fozzil_local_dev_token_9988' : '';
      window.location.replace(`${authHubLogoutUrl}?redirect=${encodeURIComponent(returnUrl)}&shade_token=${encodeURIComponent(payload)}${devAuth}`);

    } catch (error) {
      // Force clear
      setUser(null);
      setIsAuthenticated(false);
      setIsLoggingOut(false);
      window.location.replace('/');
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (email, password, metadata) => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      });

      return { data, error };
    } catch (error) {
      return { data: null, error };
    } finally {
      setIsLoading(false);
    }
  };

  // --- 15-MINUTE INACTIVITY SESSION MANAGEMENT ---
  const [showInactivityPopup, setShowInactivityPopup] = useState(false);
  const inactivityTimerRef = React.useRef(null);
  const popupTimerRef = React.useRef(null);

  const INACTIVITY_MS = 15 * 60 * 1000; // 15 minutes
  const POPUP_TIMEOUT_MS = 60 * 1000; // 60 seconds to respond before auto-logout
  const LAST_ACTIVE_KEY = 'fozzil_main_last_active';

  const resetInactivityTimer = React.useCallback(() => {
    if (!isAuthenticated) return;

    // Update shared last active cookie
    sharedInactivityStorage.setSimple(LAST_ACTIVE_KEY, Date.now().toString());

    // Clear existing timers
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (popupTimerRef.current) clearTimeout(popupTimerRef.current);
    setShowInactivityPopup(false);

    // Set new inactivity timer
    inactivityTimerRef.current = setTimeout(() => {
      setShowInactivityPopup(true);

      // Auto-logout after 60 seconds if no response
      popupTimerRef.current = setTimeout(() => {
        setShowInactivityPopup(false);
        logout();
      }, POPUP_TIMEOUT_MS);
    }, INACTIVITY_MS);
  }, [isAuthenticated]);

  const handleKeepSession = React.useCallback(() => {
    setShowInactivityPopup(false);
    if (popupTimerRef.current) clearTimeout(popupTimerRef.current);
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const handleInactivityLogout = React.useCallback(() => {
    setShowInactivityPopup(false);
    if (popupTimerRef.current) clearTimeout(popupTimerRef.current);
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    logout();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      // Clear timers when not authenticated
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (popupTimerRef.current) clearTimeout(popupTimerRef.current);
      return;
    }

    // Start tracking activity
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    // Throttle to avoid excessive updates
    let lastUpdate = 0;
    const throttledReset = () => {
      const now = Date.now();
      if (now - lastUpdate > 30000) { // Update at most every 30 seconds
        lastUpdate = now;
        resetInactivityTimer();
      }
    };

    // Initial timer start
    resetInactivityTimer();

    events.forEach(event => window.addEventListener(event, throttledReset, { passive: true }));

    return () => {
      events.forEach(event => window.removeEventListener(event, throttledReset));
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (popupTimerRef.current) clearTimeout(popupTimerRef.current);
    };
  }, [isAuthenticated, resetInactivityTimer]);

  const value = {
    user,
    isLoading,
    isAuthenticated,
    isRecovering,
    setIsRecovering,
    login,
    logout,
    signup,
    notifySecurityChange: (type) => notifySecurityChange(user, type),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* Logout Overlay - Premium Style */}
      {isLoggingOut && (
        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center animate-in fade-in duration-300">
          <div className="flex flex-col items-center gap-6">
            <div className="relative flex items-center justify-center w-16 h-16">
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/40 via-cyan-400/30 to-blue-500/40 blur-xl rounded-full taels-gemini-glow" />
              <div className="absolute -inset-1 rounded-full taels-conic-ring pointer-events-none" />
              <div className="relative z-10 w-11 h-11 rounded-full overflow-hidden flex items-center justify-center bg-black shadow-2xl border border-white/20 taels-gemini-pulse">
                <img src="/taels-logo-black.png" alt="TAELS" className="w-full h-full rounded-full object-cover select-none pointer-events-none" />
              </div>
            </div>
            <div className="flex flex-col items-center gap-1">
              <h3 className="text-white text-lg font-medium tracking-tight">FOZZIL ID</h3>
              <p className="bg-gradient-to-r from-blue-400 via-cyan-300 to-blue-500 bg-clip-text text-transparent text-sm font-medium animate-pulse">Signing out...</p>
            </div>
          </div>
        </div>
      )}
      {/* Inactivity Session Popup */}
      {showInactivityPopup && (
        <div className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm flex items-center justify-center animate-in fade-in duration-300">
          <div className="bg-[#111] border border-white/10 rounded-3xl p-8 max-w-sm w-full mx-4 shadow-2xl text-center">
            <div className="w-16 h-16 bg-[#065DC0]/10 rounded-full flex items-center justify-center mx-auto mb-6" style={{ boxShadow: '0 0 30px 8px rgba(6,93,192,0.2)' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#065DC0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
            </div>
            <h2 className="text-white text-xl font-medium mb-2">Session Expiring</h2>
            <p className="text-gray-400 text-sm mb-8">You've been inactive for 15 minutes. Your session will expire soon for security.</p>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleKeepSession}
                className="w-full bg-[#065DC0] text-white font-medium py-3.5 rounded-full hover:bg-[#054ba0] transition-all shadow-lg shadow-[#065DC0]/20"
              >
                Keep Session
              </button>
              <button
                onClick={handleInactivityLogout}
                className="w-full text-gray-400 hover:text-white font-medium py-3 rounded-full hover:bg-white/5 transition-all"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};
